import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { search, type EntityType } from '@/api/search';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { SearchInput } from './SearchInput';
import { ResultsPanel } from './ResultsPanel';
import { TypeFilterDropdown } from './TypeFilterDropdown';
import { CustomerFilterDropdown } from './CustomerFilterDropdown';
import { DateRangeFilter } from './DateRangeFilter';
import { StatusFilter, type StatusValue } from './StatusFilter';
import { ActiveFilterChips } from './ActiveFilterChips';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;

interface FilterState {
  types: EntityType[];
  customerId: string | null;
  dateFrom: string;
  dateTo: string;
  status: StatusValue;
}

const INITIAL_FILTERS: FilterState = {
  types: [],
  customerId: null,
  dateFrom: '',
  dateTo: '',
  status: 'active',
};

export function GlobalSearchPanel() {
  const user = useCurrentUser();
  const customerCtxId = useEffectiveCustomerId();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const enabled = debounced.length >= MIN_CHARS;

  // Customer scoping: when the admin/reseller has "entered" a customer
  // (banner active), pass that id so the backend scopes results. The
  // filter dropdown's customerId wins if set explicitly. Without this,
  // super_admin's JWT has no customer_id and results leak across the
  // whole tenant — the bug Sam hit 2026-05-26.
  const effectiveCustomerId = filters.customerId ?? customerCtxId ?? undefined;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['search', debounced, JSON.stringify(filters), effectiveCustomerId],
    queryFn: () => search.run({
      q: debounced,
      types: filters.types.length > 0 ? filters.types : undefined,
      customer_id: effectiveCustomerId,
      date_from: filters.dateFrom || undefined,
      date_to: filters.dateTo || undefined,
      include_inactive: filters.status === 'include_inactive',
      include_deleted: filters.status === 'include_deleted',
    }),
    enabled,
    staleTime: 30_000,
  });

  function removeType(t: EntityType) {
    setFilters({ ...filters, types: filters.types.filter((x) => x !== t) });
  }
  function clearCustomer() { setFilters({ ...filters, customerId: null }); }
  function clearDate()     { setFilters({ ...filters, dateFrom: '', dateTo: '' }); }
  function resetStatus()   { setFilters({ ...filters, status: 'active' }); }

  // Customer dropdown hidden for lower roles (RLS already scopes them) and
  // hidden when "in" a customer (banner already shows the active scope).
  const showCustomerDropdown =
    (user.role === 'super_admin' || user.role === 'reseller_owner') && !customerCtxId;

  return (
    <div className="w-full">
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder="Search recordings, callers, IVR nodes…"
      />
      {enabled && (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <TypeFilterDropdown
              value={filters.types}
              onChange={(types) => setFilters({ ...filters, types })}
            />
            {showCustomerDropdown && (
              <CustomerFilterDropdown
                value={filters.customerId}
                onChange={(customerId) => setFilters({ ...filters, customerId })}
              />
            )}
            <DateRangeFilter
              from={filters.dateFrom}
              to={filters.dateTo}
              onChange={({ from, to }) => setFilters({ ...filters, dateFrom: from, dateTo: to })}
            />
            <StatusFilter
              value={filters.status}
              onChange={(status) => setFilters({ ...filters, status })}
            />
          </div>
          <ActiveFilterChips
            types={filters.types}
            removeType={removeType}
            customerId={filters.customerId}
            clearCustomer={clearCustomer}
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
            clearDate={clearDate}
            status={filters.status}
            resetStatus={resetStatus}
          />
          <ResultsPanel
            query={debounced}
            data={data}
            isLoading={isLoading}
            isError={isError}
          />
        </>
      )}
    </div>
  );
}
